import type { FieldDefinition } from "@/shared/contracts/structure";
import { say } from "../say";

/**
 * The contract EVERY widget honours (I-F6).
 *
 * It is what makes `DynamicForm` stupid: the form asks the registry for a
 * component and renders it with these props. It never learns that a signature is
 * a canvas or that a rating is five stars, so adding an input is one file plus
 * one line in the registry — never a branch in the renderer (ADR-F5).
 */
export interface WidgetProps {
	readonly field: FieldDefinition;
	readonly modelValue: unknown;
	readonly disabled?: boolean;
	/** A server-side error already mapped to this field. */
	readonly error?: string;
	/** Present on a published form, absent inside the panel. */
	readonly publicSlug?: string;
	/**
	 * Valores deste campo JÁ TOMADOS por outros registros — só chega quando o
	 * campo é chave única de um campo só (o 1:1). O widget de referência filtra
	 * as opções; a VERDADE continua sendo o índice do banco no salvar.
	 */
	readonly taken?: ReadonlySet<string>;
}

export interface WidgetEmits {
	(event: "update:modelValue", value: unknown): void;
	(event: "blur"): void;
}

/**
 * Client-side validation, per widget.
 *
 * Deliberately a plain function rather than a method on the component: the form
 * has to validate a field the person never focused, and reaching into child
 * components to do that is how validation ends up living in three places.
 *
 * Every message mirrors what the API would say. Being told two different things
 * by two layers is worse than being told nothing by one.
 */
export type Validator = (value: unknown, field: FieldDefinition) => string | null;

/**
 * UM NÚMERO DIGITADO, lido na língua do workspace — e recusado quando há dúvida.
 *
 * O "último separador é o decimal" de antes lia "1.000" como 1 e o `type=number`
 * do navegador fazia "2,5" virar 25, calados (QA 16). Agora vale o que a língua
 * escreve: em pt-BR "1.000" é mil e "2,5" é dois e meio; em inglês, o contrário.
 * O milhar é opcional mas, se vier, em grupos de três; um inteiro não leva o
 * separador decimal. Vazio é `null`; o que não se lê sem dúvida é `undefined`.
 * O símbolo da moeda e os espaços saem antes de ler.
 */
export function readNumber(
	raw: string,
	locale: string,
	integer: boolean,
): number | null | undefined {
	// Um valor colado com a moeda ("R$ 99,90", "US$ 5", "12 €") é o número dele (QA 18).
	const s = raw.replace(/[A-Z]{0,3}\p{Sc}|\s/gu, "");
	if (s === "") return null;
	const { group, decimal } = separators(locale);
	const g = escapeRegExp(group);
	// "4.5" em pt-BR (ou "4,5" em en): um separador só, sem as três casas de um milhar,
	// é a casa decimal escrita do outro jeito. "1.889" continua mil oitocentos e oitenta e nove.
	const other =
		integer || s.includes(decimal) ? null : s.match(new RegExp(`^([+-]?\\d+)${g}(\\d+)$`));
	if (other?.[1] && other[2] && other[2].length !== 3) return Number(`${other[1]}.${other[2]}`);
	const fraction = integer ? "" : `(${escapeRegExp(decimal)}\\d+)?`;
	// Um milhar que é espaço (fr) já saiu com os brancos: sobra o `\d+`.
	if (!new RegExp(`^[+-]?(\\d+|\\d{1,3}(${g}\\d{3})+)${fraction}$`).test(s)) return undefined;
	return Number(s.split(group).join("").replace(decimal, "."));
}

/** O milhar e o decimal como `locale` os escreve. */
export function separators(locale: string): { group: string; decimal: string } {
	const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
	return {
		group: parts.find((p) => p.type === "group")?.value ?? ",",
		decimal: parts.find((p) => p.type === "decimal")?.value ?? ".",
	};
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * O que a pessoa digitou numa caixa de número e não deu para ler: o widget
 * emite ISTO, e não o texto, porque "2.5" digitado em pt-BR e o "2.5" que o banco
 * devolve seriam a mesma string. A validação recusa-o com o porquê; se escapar
 * para a rede, vai o texto cru e o servidor recusa também (`EXPECTED_NUMBER`).
 */
export class UnreadNumber {
	constructor(
		readonly text: string,
		/** Como a língua escreveria um número válido para este campo. */
		readonly example: string,
		readonly integer: boolean,
	) {}
	toJSON(): string {
		return this.text;
	}
	/** Um inteiro com vírgula ou ponto é uma casa decimal; o resto não se leu. */
	message(): string {
		return this.integer && /[.,]/.test(this.text)
			? say("wholeNumber")
			: say("unreadNumber", { example: this.example });
	}
}

/** Os minutos de atraso que ainda contam como agora — o `AT_GRACE_MS` do servidor. */
const FUTURE_GRACE_MS = 5 * 60_000;

export function isBlank(value: unknown): boolean {
	// 0 and false are ANSWERS. Treating them as blank is how a rating of zero or
	// an unchecked box reads as "not filled in" and blocks a valid submission.
	return (
		value === null ||
		value === undefined ||
		value === "" ||
		(Array.isArray(value) && value.length === 0)
	);
}

/** The one rule shared by every widget: required means present. */
export function requiredValidator(value: unknown, field: FieldDefinition): string | null {
	if (field.required && isBlank(value)) return say("required", { label: field.label });
	return null;
}

/** A widget's own extra rule, run after `required`. */
export const VALIDATORS: Record<string, Validator> = {
	email: (value) => {
		if (typeof value !== "string" || value.length === 0) return null;
		return /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(value) ? null : say("invalidEmail");
	},
	phone: (value) => {
		if (typeof value !== "string" || value.length === 0) return null;
		// The same rule and the same reason as the API: a national number is
		// ambiguous across countries, so it is refused rather than guessed.
		return value.trim().startsWith("+") ? null : say("invalidPhone");
	},
	url: (value) => {
		if (typeof value !== "string" || value.length === 0) return null;
		try {
			new URL(value);
			return null;
		} catch {
			return say("invalidUrl");
		}
	},
	// O mesmo que o servidor recusa (`EXPECTED_INTEGER`): 2,5 não cabe num inteiro.
	integer: (value) =>
		typeof value === "number" && !Number.isInteger(value) ? say("wholeNumber") : null,
	time: (value) => {
		if (typeof value !== "string" || value.length === 0) return null;
		return /^\d{2}:\d{2}(:\d{2})?$/.test(value) ? null : say("invalidTime");
	},
	/**
	 * "Quando enviar" não aceita ontem — mas só quando o CAMPO o pede.
	 *
	 * Um datetime também guarda o que já aconteceu (a data de abertura, o último
	 * contato), por isso a regra é opt-in por `config.future` e não uma lei de
	 * todos os datetimes. A tolerância é a mesma do `create_schedule` no servidor:
	 * escolher 19:22 e salvar às 19:26 é quem estava a acabar de escrever, e as
	 * duas camadas têm de dizer a MESMA coisa — ser recusado aqui por algo que o
	 * servidor aceitaria é pior do que não ser avisado de nada.
	 */
	datetime: (value, field) => {
		if (field.config.future !== true) return null;
		if (typeof value !== "string" || value.length === 0) return null;
		const at = new Date(value).getTime();
		if (Number.isNaN(at)) return null;
		return Date.now() - at > FUTURE_GRACE_MS ? say("mustBeFuture") : null;
	},
	json: (value) => {
		if (typeof value !== "string" || value.trim().length === 0) return null;
		try {
			JSON.parse(value);
			return null;
		} catch {
			return say("invalidJson");
		}
	},
};

/** Both halves of validation for one field, in the order a person expects. */
export function validate(value: unknown, field: FieldDefinition): string | null {
	const missing = requiredValidator(value, field);
	if (missing) return missing;
	if (value instanceof UnreadNumber) return value.message();
	return VALIDATORS[field.type]?.(value, field) ?? null;
}
